<script setup lang="ts">
/**
 * The id-keyed list of one exercise — options, pairs, items, ticks or steps: reorder, remove, add.
 * The row's own fields come from the sub-form through the `item` slot, which is handed the row and
 * an `update` writer bound to it.
 */
import type { MathExercise, MathExerciseListKey } from '../../../../model/assignments/store/interfaces'
import type { MathExerciseListRow } from '../../../../model/assignments/math-exercise-editors.hook'

import { faArrowDown, faArrowUp, faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import { useMathExerciseItemList } from '../../../../model/assignments/math-exercise-editors.hook'

defineOptions({
  name: 'ExerciseItemList',
})

const props = withDefaults(defineProps<{
  exercise: MathExercise
  list: MathExerciseListKey
  /** `option`, `pair`… — names the controls ("Move option up"). */
  noun: string
  label: string
  min?: number
  max?: number
}>(), {
  min: 2,
  max: undefined,
})

defineSlots<{
  item: (scope: {
    row: MathExerciseListRow
    update: (patch: Record<string, string>) => void
  }) => unknown
}>()

const {
  refRows,
  refCanAdd,
  refCanRemove,
  addItem,
  removeItem,
  moveItemUp,
  moveItemDown,
  updateItem,
} = useMathExerciseItemList({
  exercise: () => props.exercise,
  list: () => props.list,
  min: () => props.min,
  max: () => props.max,
})
</script>

<template>
  <div class="exercise-item-list">
    <AppText name="body2-medium">
      {{ label }}
    </AppText>

    <ul class="exercise-item-list__items">
      <li
        v-for="row in refRows"
        :key="row.id || `row-${row.position}`"
        class="exercise-item-list__item"
      >
        <AppText
          name="body2"
          color="slate-500"
          class="exercise-item-list__position"
        >
          {{ row.position }}.
        </AppText>

        <div class="exercise-item-list__fields">
          <slot
            name="item"
            :row="row"
            :update="(patch: Record<string, string>) => updateItem(row.id, patch)"
          />
        </div>

        <div class="exercise-item-list__actions">
          <BtnIcon
            :icon="faArrowUp"
            size="small"
            :title="`Move ${noun} up`"
            :disabled="row.isFirstInOrder"
            @click="moveItemUp(row.id)"
          />
          <BtnIcon
            :icon="faArrowDown"
            size="small"
            :title="`Move ${noun} down`"
            :disabled="row.isLastInOrder"
            @click="moveItemDown(row.id)"
          />
          <BtnIcon
            :icon="faTrashCan"
            size="small"
            color="red"
            :title="`Remove ${noun}`"
            :disabled="!refCanRemove"
            @click="removeItem(row.id)"
          />
        </div>
      </li>
    </ul>

    <BtnSecondary
      :text="`Add ${noun}`"
      :disabled="!refCanAdd"
      @click="addItem"
    />
  </div>
</template>

<style scoped lang="scss" src="./ExerciseItemList.scss" />
